import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Star, Award, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

// Bokeh elementleri - sabit değerler (her renderda yeniden hesaplanmaz)
const DESKTOP_BOKEH = Array.from({ length: 25 }, (_, i) => ({
  id: i,
  width: 20 + (i * 7) % 40,
  height: 20 + (i * 11) % 40,
  left: (i * 17) % 100,
  delay: (i * 0.4) % 10,
  duration: 10 + (i * 3) % 8
}));

const MOBILE_BOKEH = Array.from({ length: 15 }, (_, i) => ({
  id: i,
  width: 15 + (i * 5) % 30,
  height: 15 + (i * 7) % 30,
  left: (i * 23) % 100,
  delay: (i * 0.5) % 8,
  duration: 8 + (i * 2) % 6
}));

const AboutUs = () => {
  const { t, language } = useLanguage();
  
  // Sayfa yüklendiğinde en üste scroll
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Dil bazlı rezervasyon path
  const reservationsPath = language === 'de' ? '/reservierungen' : '/reservations';
  
  // SVG/WebP icons for service cards - local optimized files
  const serviceIconUrls = {
    0: './images/food-truck.svg',
    1: './images/garden-party.svg',
    2: './images/cocktail-events.svg',
    3: './images/birthday.webp',
    4: './images/corporate-events.webp',
    5: './images/dream-event.svg'
  };
  
  // Emoji fallbacks for icons without SVG
  const serviceEmojis = {
    0: '🚚',  // Food Truck Service
    1: '⛺',  // Garden Parties
    2: '🍻',  // Cocktail Events
    3: '🎂',  // Birthday Celebrations
    4: '🏢',  // Corporate Events
    5: '✨'   // Your Dream Event
  };

  const stats = [
    { icon: Users, value: '3000+', label: t.aboutUs.stats.clientsLabel },
    { icon: Star, value: '450+', label: t.aboutUs.stats.eventsLabel },
    { icon: Award, value: '4.8', label: t.aboutUs.stats.ratingLabel }
  ];

  return (
    <div className="min-h-screen pt-16 bg-[#f8f6f3]">
      {/* Hero Section */}
      <section className="relative h-[75vh] md:h-[650px] overflow-hidden">
        {/* Background Image - Desktop & Tablet */}
        <div 
          className="absolute inset-0 hidden md:block"
          style={{
            backgroundImage: 'url(./images/hero-main.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#0a0a0a]/80" />
          <div className="absolute inset-0 bg-black/30" />
        </div>

        {/* Mobile Background */}
        <div className="absolute inset-0 md:hidden">
          {/* Mobile Hero Image */}
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: 'url(./images/hero-main.webp)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0a0a0a]/90" />
          <div className="absolute inset-0 bg-black/20" />
        </div>
        
        {/* Mobile Layout */}
        <div className="relative z-10 flex flex-col md:hidden h-full justify-start pt-[38vh]">
          <div className="flex flex-col items-center justify-center px-6 text-center">
            <img 
              src="./images/slai-logo-transparent.png" 
              alt="SLÁI Events" 
              className="h-16 object-contain invert mb-3"
            />
            <div className="w-16 h-[1px] bg-white/60 mb-3" />
            <p className="text-xl font-bold tracking-wide text-white mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {t.company.tagline}
            </p>
            <p className="text-sm text-white/80 leading-relaxed max-w-xs mb-8" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
              {t.company.description}
            </p>
          </div>
        </div>

        {/* Desktop & Tablet Layout */}
        <div className="relative z-10 h-full hidden md:flex items-center justify-center">
          <div className="flex flex-col items-center justify-center px-8 ml-[50%]">
              <div className="flex items-center justify-center mb-0 w-full">
                <img 
                  src="./images/slai-logo-transparent.png" 
                  alt="SLÁI Events" 
                  className="h-16 md:h-18 lg:h-20 object-contain invert"
                />
              </div>
              <div className="w-20 h-[1px] bg-white/60 mt-3" />
              <p className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-wide text-white mt-3 text-center" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {t.company.tagline}
              </p>
              <p className="text-base md:text-lg text-white/80 leading-relaxed max-w-lg mt-2 text-center" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                {t.company.description}
              </p>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce hidden sm:block">
          <div className="w-6 h-10 border border-white/40 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/40 rounded-full" />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="pt-6 pb-16 md:py-24 bg-[#1a1a1a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-20">
            <p className="text-xs tracking-[0.3em] uppercase text-[#8b7355] mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {language === 'de' ? 'UNSERE DIENSTLEISTUNGEN' : 'OUR SERVICES'}
            </p>
            <h2 className="text-4xl sm:text-5xl md:text-6xl text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
              {t.aboutUs.servicesTitle}
            </h2>
            <div className="w-16 h-[1px] bg-white/40 mx-auto mb-6" />
            <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
              {t.aboutUs.servicesSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-7xl mx-auto">
            {t.aboutUs.services.map((service, index) => {
              return (
                <Card
                  key={index}
                  className="rounded-xl border border-white/20 bg-[#252525] shadow-none hover:bg-[#2a2a2a] transition-all duration-500 overflow-hidden group"
                >
                  <CardContent className="p-8 sm:p-10">
                    {serviceIconUrls[index] ? (
                      <div 
                        className="w-16 h-16 sm:w-20 sm:h-20 mb-6"
                        style={{
                          backgroundImage: `url(${serviceIconUrls[index]})`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'center',
                          backgroundSize: 'contain',
                          backgroundColor: 'transparent'
                        }}
                      />
                    ) : (
                      <div className="w-16 h-16 sm:w-20 sm:h-20 mb-6 flex items-center justify-center" style={{ backgroundColor: 'transparent' }}>
                        <span className="text-5xl sm:text-6xl" style={{ background: 'transparent' }}>{serviceEmojis[index]}</span>
                      </div>
                    )}
                    <h3 className="text-xl sm:text-2xl text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>{service.title}</h3>
                    <p className="text-sm text-white/60 mb-6" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>{service.description}</p>
                    <ul className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-3 text-white/70">
                          <div className="w-1 h-1 bg-[#8b7355] flex-shrink-0" />
                          <span className="text-xs" style={{ fontFamily: "'Montserrat', sans-serif" }}>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Single Book Now CTA Button */}
          <div className="text-center mt-16">
            <Link to={reservationsPath}>
              <Button
                className="bg-[#3a3a3a] text-white border border-[#e67e22] hover:bg-[#4a4a4a] text-xs tracking-[0.2em] uppercase px-12 py-6 rounded-none"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {t.aboutUs.bookNow}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 sm:py-24 bg-[#1a1a1a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs tracking-[0.3em] uppercase text-[#8b7355] mb-4 text-center" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {language === 'de' ? 'UNSERE GESCHICHTE' : 'OUR STORY'}
            </p>
            <h2 className="text-4xl sm:text-5xl text-white mb-6 text-center" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
              {t.aboutUs.storyTitle}
            </h2>
            <div className="w-16 h-[1px] bg-white/40 mx-auto mb-12" />
            <div className="space-y-6 sm:space-y-8 text-base text-white/70">
              {t.aboutUs.storyParagraphs.map((paragraph, index) => (
                <p key={index} className="text-center" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Beyaz arka plan */}
      <section className="py-16 sm:py-24 bg-[#f8f6f3]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-6 sm:gap-12 max-w-5xl mx-auto">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className="text-center group"
                >
                  <div className="inline-flex items-center justify-center w-14 h-14 sm:w-20 sm:h-20 rounded-full border border-[#1a1a1a]/20 mb-4 sm:mb-6 group-hover:bg-[#1a1a1a] transition-all duration-300">
                    <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-[#1a1a1a] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <div className="text-3xl sm:text-5xl text-[#1a1a1a] mb-2 sm:mb-3 font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{stat.value}</div>
                  <div className="text-xs tracking-[0.15em] uppercase text-[#6b6b6b]" style={{ fontFamily: "'Montserrat', sans-serif" }}>{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
